function fitColumnWidths(widths, available, minWidth) {
  const safe = widths.map(width => Math.max(minWidth, Number(width) || 15));
  const total = safe.reduce((sum, width) => sum + width, 0) || safe.length;
  let scaled = safe.map(width => available * width / total);
  const mins = scaled.map(width => Math.min(Math.max(minWidth, width * 0.55), available / Math.max(1, widths.length)));
  scaled = scaled.map((width, index) => Math.max(width, mins[index]));
  for (let pass = 0; pass < 8; pass += 1) {
    const extra = scaled.reduce((sum, width) => sum + width, 0) - available;
    if (Math.abs(extra) <= 0.05) break;
    if (extra > 0) {
      const slack = scaled.map((width, index) => Math.max(0, width - mins[index]));
      const slackTotal = slack.reduce((sum, width) => sum + width, 0);
      if (!(slackTotal > 0)) break;
      scaled = scaled.map((width, index) => width - extra * (slack[index] / slackTotal));
    } else {
      scaled = scaled.map(width => width - extra / scaled.length);
    }
  }
  const sum = scaled.reduce((total, width) => total + width, 0) || 1;
  return scaled.map(width => width * available / sum);
}

function cellText(cell) {
  const value = cell.value;
  if (value instanceof Date) {
    return `${String(value.getUTCDate()).padStart(2, '0')}-${String(value.getUTCMonth() + 1).padStart(2, '0')}-${value.getUTCFullYear()}`;
  }
  return String(cell.text || '').trim();
}

function isNumericText(value) {
  return /^-?[\d,.]+$/.test(String(value || '').trim());
}

function isDateText(value) {
  return /^\d{2}-\d{2}-\d{4}$/.test(String(value || '').trim());
}

// Reuse the report data and grouping already assembled by each export action.
export async function createWorkbookPdf(workbook) {
  const [{ jsPDF }, { autoTable }] = await Promise.all([
    import('jspdf'), import('jspdf-autotable'),
  ]);
  const widestSheet = Math.max(...workbook.worksheets.map(sheet => sheet.columnCount));
  const wide = widestSheet > 5;
  const narrow = widestSheet > 10;
  const format = narrow ? 'a3' : 'a4';
  const doc = new jsPDF({ orientation: wide ? 'landscape' : 'portrait', unit: 'mm', format });
  doc.setProperties({ title: workbook.worksheets.map(sheet => sheet.name).join(', '), creator: 'Import ERP' });
  workbook.worksheets.forEach((sheet, sheetIndex) => {
    if (sheetIndex) doc.addPage();
    const side = narrow ? 8 : 12;
    doc.setFontSize(narrow ? 13 : 16);
    doc.setTextColor(23, 52, 95);
    doc.text(sheet.name, side, 14);
    const body = [];
    const merges = (sheet.model.merges || []).map(range => {
      const [start, end] = range.split(':').map(address => {
        const [, letters, row] = address.match(/^([A-Z]+)(\d+)$/);
        const column = [...letters].reduce((total, letter) => total * 26 + letter.charCodeAt(0) - 64, 0);
        return { row: Number(row), column };
      });
      return { start, end };
    });
    for (let rowNumber = 2; rowNumber <= sheet.rowCount; rowNumber += 1) {
      const row = [];
      for (let column = 1; column <= sheet.columnCount; column += 1) {
        const merge = merges.find(({ start, end }) => rowNumber >= start.row && rowNumber <= end.row && column >= start.column && column <= end.column);
        if (merge && (rowNumber !== merge.start.row || column !== merge.start.column)) continue;
        const cell = sheet.getCell(rowNumber, column);
        const content = cellText(cell);
        row.push({
          content,
          ...(merge ? { rowSpan: merge.end.row - merge.start.row + 1, colSpan: merge.end.column - merge.start.column + 1 } : {}),
          styles: {
            fontStyle: cell.font?.bold ? 'bold' : 'normal',
            overflow: isDateText(content) || isNumericText(content) ? 'ellipsize' : 'linebreak',
            halign: isNumericText(content) ? 'right' : 'left',
          },
        });
      }
      body.push(row);
    }
    const availableWidth = doc.internal.pageSize.getWidth() - side * 2;
    const minWidth = narrow ? 7 : 10;
    const fitted = fitColumnWidths(sheet.columns.map(column => column.width || 15), availableWidth, minWidth);
    autoTable(doc, {
      startY: 18,
      margin: { left: side, right: side, top: 10, bottom: 16 },
      tableWidth: availableWidth,
      head: [sheet.columns.map((_, index) => cellText(sheet.getCell(1, index + 1)))],
      body,
      theme: 'grid',
      showHead: 'everyPage',
      styles: {
        fontSize: narrow ? 7 : wide ? 8 : 10,
        cellPadding: narrow ? 0.8 : 2,
        overflow: 'linebreak',
        minCellWidth: minWidth,
        valign: 'middle',
        textColor: [23, 52, 95],
        lineColor: [160, 175, 190],
        lineWidth: 0.15,
      },
      headStyles: {
        fillColor: [228, 237, 245],
        textColor: [23, 52, 95],
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        overflow: 'linebreak',
        cellPadding: narrow ? 1 : 2,
        fontSize: narrow ? 6.5 : wide ? 8 : 10,
      },
      columnStyles: Object.fromEntries(fitted.map((cellWidth, index) => [index, { cellWidth }])),
      rowPageBreak: 'avoid',
      horizontalPageBreak: false,
    });
  });
  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    doc.setFontSize(8);
    doc.setTextColor(100);
    const side = narrow ? 8 : 12;
    doc.text(`Page ${page} of ${pages}`, doc.internal.pageSize.getWidth() - side, doc.internal.pageSize.getHeight() - 7, { align: 'right' });
  }
  return doc.output('blob');
}
